import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";
import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { LockKey, Warning } from "@phosphor-icons/react";
import PasswordStrengthMeter from "../components/PasswordStrengthMeter";

export default function ResetPassword() {
  const { token } = useParams();
  const t = useT();
  const nav = useNavigate();
  const { setUser } = useAuth();

  const [verifying, setVerifying] = useState(true);
  const [valid, setValid] = useState(false);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [pwValid, setPwValid] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/auth/reset-password/verify?token=${encodeURIComponent(token || "")}`)
      .then((r) => {
        if (!cancelled) setValid(!!r.data?.valid);
      })
      .catch(() => {
        if (!cancelled) setValid(false);
      })
      .finally(() => {
        if (!cancelled) setVerifying(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) {
      toast.error(t("reset.error_min_length"));
      return;
    }
    if (pw !== pw2) {
      toast.error(t("reset.error_mismatch"));
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/auth/reset-password", {
        token,
        new_password: pw,
      });
      // Defensive: if the server somehow omits the JWT, send the user to
      // /login so they can sign in manually rather than a silent 401 loop.
      if (!r.data?.token) {
        toast.success(t("reset.toast_success"));
        nav("/login");
        return;
      }
      // Sign the user in immediately with the freshly issued token.
      localStorage.setItem("cr_token", r.data.token);
      sessionStorage.removeItem("cr_token");
      setUser(r.data.user);
      toast.success(t("reset.toast_success"));
      const role = r.data?.user?.role;
      nav(role === "admin" ? "/admin" : role === "manager" ? "/manager" : role === "agent" ? "/agent" : "/dashboard");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("reset.toast_failed"));
      // If the link is now invalid (consumed elsewhere or expired between verify
      // and submit), flip back to the invalid state so the user can request a new one.
      if (err?.response?.status === 400) setValid(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF9F6]">
      <Header />
      <div className="max-w-md mx-auto px-6 py-20">
        <p className="text-xs tracking-[0.3em] uppercase text-[#4A5C50] mb-3">
          {t("reset.eyebrow")}
        </p>
        <h1 className="serif text-5xl">{t("reset.title")}</h1>

        {verifying && (
          <p className="mt-8 text-sm text-[#4A5C50]" data-testid="reset-verifying">
            {t("reset.verifying")}
          </p>
        )}

        {!verifying && !valid && (
          <div
            className="mt-10 rounded-2xl border border-[#CBA35C]/40 bg-[#CBA35C]/10 p-5"
            data-testid="reset-invalid-banner"
          >
            <div className="flex items-start gap-3">
              <Warning size={20} weight="duotone" className="text-[#8C6B21] shrink-0 mt-0.5" />
              <div className="text-sm text-[#8C6B21]">
                <p className="font-semibold">{t("reset.invalid_title")}</p>
                <p className="mt-1 text-[#4A5C50]">{t("reset.invalid_message")}</p>
                <Link
                  to="/forgot-password"
                  className="inline-block mt-4 text-[#184A30] link-ul"
                  data-testid="reset-request-new-link"
                >
                  {t("reset.request_new_link")}
                </Link>
              </div>
            </div>
          </div>
        )}

        {!verifying && valid && (
          <>
            <p className="text-sm text-[#4A5C50] mt-4">{t("reset.subtitle")}</p>
            <form onSubmit={submit} className="mt-10 space-y-6" data-testid="reset-password-form">
              <div>
                <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">
                  {t("reset.new_password_label")}
                </Label>
                <div className="relative">
                  <LockKey size={18} weight="duotone" className="absolute left-0 top-1/2 -translate-y-1/2 text-[#4A5C50] pointer-events-none" />
                  <input
                    className="underline-input mono pl-8"
                    type="password"
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    minLength={8}
                    required
                    autoFocus
                    data-testid="reset-password-new"
                  />
                </div>
                <PasswordStrengthMeter password={pw} onValidityChange={setPwValid} />
              </div>
              <div>
                <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">
                  {t("reset.confirm_password_label")}
                </Label>
                <input
                  className="underline-input mono"
                  type="password"
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  minLength={8}
                  required
                  data-testid="reset-password-confirm"
                />
              </div>
              <Button
                type="submit"
                disabled={loading || !pwValid || pw !== pw2}
                className="w-full h-12 rounded-xl bg-[#184A30] hover:bg-[#0F3320] disabled:bg-[#184A30]/40 text-white"
                data-testid="reset-password-submit"
              >
                {loading ? t("reset.submitting") : t("reset.submit")}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
